<template>
  <div class="w-full flex flex-col flex-grow items-center">
    <WorkingAndError :isWorking="isWorking" :error="responseError" @clearError="responseError = null" />
    <div v-if="equipment && typeof equipment === 'object'" class="w-full flex flex-col flex-grow min-h-0 items-center max-w-6xl">
<!-- Box Number, Kit, History, Damage Report toggle — always visible -->
      <div class="w-full flex flex-wrap items-center border-b bg-neutral-100 dark:bg-neutral-800 z-10 flex-shrink-0">
        <div class="flex-grow flex flex-nowrap items-center p-1 min-w-64">
          <div class="flex-grow text-left px-1 whitespace-normal truncate p-1 text-sm font-semibold">
              {{`
              Box ${formatBoxNumber(equipment.boxNumber, equipment.boxYear)} ${equipment.projectId?._id
              ? equipment.projectId?.companyProjectNumber
              ? ' - ' + equipment.projectId?.companyProjectNumber : '' : ''}
              ${equipment.projectId?.name ? ' - ' + equipment.projectId?.name : ''}
              `}}
              <span v-if="equipment.returnDisposition" class="text-red p-1">{{ capitalizeString(equipment.returnDisposition) }}</span>
          </div>
          <div v-if="equipment.isKit || equipment.kitId" class="text-sky font-semibold text-sm py-0.5 px-1">
              {{`${equipment.isKit ? 'Kit' : 'in Kit'}`}}
          </div>
          <div v-if="equipment.boxSealed" class="text-amber font-semibold text-sm py-0.5 px-1">
              Sealed Box
          </div>
          <div v-else-if="equipment.multiBox" class="text-amber font-semibold text-sm py-0.5 px-1">
              Multi-Piece
          </div>
          <div class="w-auto flex-shrink-0 p-1">
            <Popper hover arrow placement="top" content="History">
              <button class="bn-icon-only" @click="showHistory = true">
                  <MdManageHistory />
              </button>
            </Popper>
          </div>
        </div>
      </div>
<!-- Multi-Piece summary -->
      <div v-if="equipment.multiBox && equipment.multiBoxSummary" class="w-full flex flex-wrap items-center pl-2 border-b">
          <div class="w-auto p-1 flex flex-nowrap items-center">
              <span class="opacity-70 mr-1">Qty:</span>
              <span class="font-semibold text-sky">{{ equipment.multiBoxSummary.qty }}</span>
          </div>
          <div class="w-auto p-1 flex flex-nowrap items-center">
              <span class="opacity-70 mr-1">Assigned:</span>
              <span class="font-semibold text-sky">{{ equipment.multiBoxSummary.assigned }}</span>
          </div>
          <div class="w-auto p-1 flex flex-nowrap items-center">
              <span class="opacity-70 mr-1">Available:</span>
              <span class="font-semibold text-sky">{{ equipment.multiBoxSummary.available }}</span>
          </div>
      </div>
      <div v-if="equipment?.returnDisposition === 'damaged' || equipment?.damageDecision" class="w-full flex items-center p-1 border-y">
          <button class="bn-text text-red" @click="openDamageReport">
            <DocumentIcon />
            <div>Damage Report</div>
          </button>
      </div>
<!-- Main box detail -->
      <div class="w-full flex flex-col flex-auto flex-grow h-0 overflow-y-auto px-2 pb-2">
<!-- Manufacturer, Part #, Quantity -->
        <div class="w-full flex items-center flex-wrap">
            <div class="w-full sm:w-auto sm:flex-grow sm:min-w-64 sm:max-w-96 flex-col p-1 flex items-center">
                <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ String(equipment.alias?.manufacturer || equipment.manufacturer || '').toUpperCase() }}</div>
                <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Manufacturer</div>
            </div>
                <div class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ String(equipment.alias?.partNumber || equipment.partNumber || '').toUpperCase() }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Part #</div>
                </div>
                <!-- Alt Part # — catalog-owned, read-only; stripped server-side from external views -->
                <div v-if="equipment.altPartNumber" class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ String(equipment.altPartNumber).toUpperCase() }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Alt Part #</div>
                </div>
                <!-- Color — catalog-owned, read-only -->
                <div v-if="equipment.color" class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ String(equipment.color || '').toUpperCase() }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Color</div>
                </div>
                <div class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                  <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{equipment.barCode}}</div>
                  <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">UPC</div>
                </div>
                <div class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal px-2 p-1">{{ equipment.createdAt && isValid(parseISO(equipment.createdAt)) ? format(parseISO(equipment.createdAt), 'MM/dd/yyyy') : '—' }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Received</div>
                </div>
                <div class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ equipment.trackingNumber }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Tracking #</div>
                </div>
                <div v-if="equipment.purchaseOrder" class="w-full min-w-0 flex-col sm:w-44 sm:min-w-44 sm:max-w-44 p-1 flex items-center">
                    <div class="w-full text-left whitespace-normal break-words px-2 p-1">{{ capitalizeString(equipment.purchaseOrder) }}</div>
                    <div class="w-full text-left whitespace-normal px-2 pt-0.5 text-[10px] text-sky">Purchase Order</div>
                </div>
        </div>
<!-- Description -->
        <div v-if="equipment.description" class="w-full flex items-center p-1 mb-1 border-b">
          <div class="w-full flex flex-col">
            <div class="w-full text-left whitespace-normal px-2 p-1 overflow-y-auto">{{ equipment.description || '' }}</div>
            <div class="w-full text-left whitespace-normal truncate px-2 pt-0.5 text-[10px] text-sky">Description</div>
          </div>
        </div>
<!-- Shipping -->
        <div v-if="equipment.shipmentId && !dontShowMultiBoxDetails" class="w-full flex flex-wrap items-center pl-2">
            <div class="w-24 min-w-24 max-w-24">
                <div v-if="equipment.shipmentId?.shipmentState" class="w-full text-left whitespace-normal truncate font-semibold p-1" :class=" equipment.shipmentId?.shipmentState > 2 ? 'text-green' : 'text-sky'">
                    {{equipment.shipmentId?.shipmentState > 2 ? 'Shipped' : 'Scheduled'}}
                </div>
                <div v-else-if="equipment.shipmentId?.shipmentDate" class="w-full text-left whitespace-normal truncate font-semibold text-red p-1">
                  {{ isValid(parseISO(equipment.shipmentId?.shipmentDate)) && isPast(parseISO(equipment.shipmentId?.shipmentDate)) ? 'Shipped' : 'Scheduled' }}
                </div>
            </div>
            <div class="w-auto sm:min-w-fit flex flex-wrap items-center">
                <div v-if="equipment.shipmentId?.shipmentDate" class="w-auto sm:min-w-fit flex items-center">
                    <div class="w-full whitespace-normal text-left truncate p-1">
                        {{ equipment.shipmentId?.shipmentDate && isValid(parseISO(equipment.shipmentId.shipmentDate)) ? format(parseISO(equipment.shipmentId.shipmentDate), 'MMM dd, yyyy h:mm aaa') : '' }}
                    </div>
                </div>
                <div v-if="equipment.shipmentId?.shipmentNumber" class="w-auto sm:min-w-fit flex items-center flex-nowrap">
                    <div class="w-auto min-w-fit opacity-70 p-1">
                        <div class="w-full text-left whitespace-normal truncate opacity-80">Shipment # :</div>
                    </div>
                    <div class="w-auto min-w-fit text-left whitespace-normal truncate p-1">
                        {{ equipment.shipmentId?.shipmentNumber || 'N/A'}}
                    </div>
                </div>
            </div>
        </div>
<!-- Rack, Room -->
        <div v-if="equipment.rackId && !dontShowMultiBoxDetails" class="w-full flex flex-wrap items-center pl-2">
            <div class="w-24 min-w-24 max-w-24 p-1">
                <div class="w-full text-left whitespace-normal truncate opacity-80">Rack :</div>
            </div>
            <div class="w-auto flex-grow text-left whitespace-normal truncate p-1">{{ equipment.rackId?.name || 'Unknown' }}</div>
        </div>
        <div v-if="equipment.roomId && !dontShowMultiBoxDetails" class="w-full flex flex-wrap items-center pl-1">
            <div class="w-24 min-w-24 max-w-24 pl-2 p-1">
                <div class="w-full text-left whitespace-normal truncate opacity-80">Room :</div>
            </div>
            <div class="w-auto flex-grow text-left whitespace-normal truncate pl-2 p-1">
                {{ roomLabel(equipment.roomId) || 'Unknown' }}
            </div>
        </div>
<!-- Serial, MAC Address, Properties (3-column display). NOT flex-grow: inside the modal's scroll
     region it would absorb all leftover vertical space on a tall screen, stretching the three boxes
     far past their content. Sized to content; items-stretch still levels the three columns. -->
        <div v-if="!dontShowMultiBoxDetails" class="w-full flex items-stretch flex-wrap px-1">
        <!-- Serial: on/off per box like the MAC; off hides the values but keeps them. Never on a multi-piece box -->
          <div v-if="equipment.hasSerial || (allowEdit && !equipment.multiBox)" class="w-full flex-col md:w-1/3 md:min-w-44 p-1 flex md:justify-start items-start md:items-center self-stretch">
            <div v-if="allowEdit && !equipment.multiBox" class="w-full flex items-center flex-wrap">
              <div class="p-1 flex-shrink-0">
                <button type="button" class="bn-text" :disabled="isWorking"
                  :class="equipment.hasSerial ? 'font-semibold text-green' : 'text-normal opacity-60'"
                  @click="updateBox('hasSerial', !equipment.hasSerial, equipment.hasSerial)">
                  <div>Has serial #</div>
                  <CheckIcon v-if="equipment.hasSerial" />
                  <XIcon v-else />
                </button>
              </div>
            </div>
            <div v-if="equipment.hasSerial" class="w-full flex items-center flex-wrap">
              <div class="w-auto min-w-fit text-left whitespace-normal text-sky p-1 mr-1">Serial #:</div>
              <div v-if="allowEdit" class="flex-grow min-w-56 max-w-64 p-1" >
                <input v-model="equipment.serialNumber" type="text" class="w-full" placeholder="serial #" 
                @focus="prevValue = equipment.serialNumber"
                @blur="prevValue !== equipment.serialNumber ? updateBox('serialNumber',equipment.serialNumber,prevValue) : null"
                @keydown.enter="$event.target.blur()"
                >
              </div>
              <div v-else class="flex-grow min-w-fit p-1 text-left whitespace-normal truncate">{{ equipment.serialNumber }}</div>
            </div>
        <!-- Other serials, as Other MAC Addresses: add by name, rename or remove any -->
            <div v-if="equipment.hasSerial && ((Array.isArray(equipment.serialNumbers) && equipment.serialNumbers.length > 0) || allowEdit)" class="w-full flex flex-col flex-grow self-stretch min-h-0">
                <div class="w-full flex items-center flex-nowrap flex-shrink-0">
                    <div class="w-auto min-w-fit text-left whitespace-normal p-1 mr-1 shrink-0">Other Serial #:</div>
                    <div v-if="canManageSlots" class="flex-grow min-w-0 p-1">
                        <input v-model="newSerialName" type="search" class="w-full" placeholder="Add serial (name)" maxlength="25" @blur="addSerial" @keydown.enter="$event.target.blur()" />
                    </div>
                </div>
                <div class="w-full min-h-32 flex flex-grow self-stretch rounded-md border">
                    <div v-if="Array.isArray(equipment.serialNumbers) && equipment.serialNumbers.length > 0" class="w-full min-h-0 p-1 max-h-32 md:max-h-48 overflow-y-auto flex-grow self-stretch">
                        <div v-for="serial in (equipment.serialNumbers || [])" :key="serial._id" class="w-full p-1">
                            <div class="w-full flex items-center flex-wrap">
                                <div v-if="allowEdit" class="flex-grow p-1 min-w-32 max-w-48">
                                    <input v-model="serial.name" type="text" class="w-full" placeholder="serial name" maxlength="25"
                                        @focus="prevValue = serial.name"
                                        @blur="prevValue !== serial.name && updateArrayEntry('serialNumbers', serial._id, { name: serial.name }, prevValue, 'name')"
                                        @keydown.enter="$event.target.blur()"
                                    />
                                </div>
                                <div v-else class="flex-grow text-left whitespace-normal truncate text-sky p-1">{{ camelCaseToLabel(serial.name) }}</div>
                                <div class="flex flex-nowrap items-center flex-grow">
                                    <div class="w-full p-1 min-w-36 max-w-64">
                                        <input v-if="allowEdit" v-model="serial.serialNumber" type="text" class="w-full" placeholder="serial #"
                                            @focus="prevValue = serial.serialNumber"
                                            @blur="prevValue !== serial.serialNumber && updateArrayEntry('serialNumbers', serial._id, { serialNumber: serial.serialNumber }, prevValue, 'serialNumber')"
                                            @keydown.enter="$event.target.blur()"
                                        />
                                        <div v-else class="w-full p-1 whitespace-normal truncate">{{ serial.serialNumber }}</div>
                                    </div>
                                    <div v-if="canManageSlots" class="p-1 flex-shrink-0">
                                        <ButtonWithConfirm isDelete :popperContent="`Remove ${camelCaseToLabel(serial.name)}`" @confirmed="deleteSerialEntry(serial._id)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="w-full p-1 text-left whitespace-normal truncate">No other serials</div>
                </div>
            </div>
          </div>
        <!-- MAC Address: on/off per box; off hides every MAC value and its Network data but keeps them (the Network tab
             lists only boxes with a MAC). Never on a multi-piece box -->
          <div v-if="equipment.hasMacAddress || (allowEdit && !equipment.multiBox)" class="w-full flex-col md:w-1/3 md:min-w-44 p-1 flex md:justify-start items-start md:items-center self-stretch">
            <div v-if="allowEdit && !equipment.multiBox" class="w-full flex items-center flex-wrap">
              <div class="p-1 flex-shrink-0">
                <button type="button" class="bn-text" :disabled="isWorking"
                  :class="equipment.hasMacAddress ? 'font-semibold text-green' : 'text-normal opacity-60'"
                  @click="updateBox('hasMacAddress', !equipment.hasMacAddress, equipment.hasMacAddress)">
                  <div>Has MAC address</div>
                  <CheckIcon v-if="equipment.hasMacAddress" />
                  <XIcon v-else />
                </button>
              </div>
            </div>
            <div v-if="equipment.hasMacAddress" class="w-full flex items-center flex-wrap">
              <div class="w-auto min-w-fit text-left whitespace-normal text-sky p-1 mr-1">{{ equipment.mainMacName ? camelCaseToLabel(equipment.mainMacName) : 'MAC Address' }}:</div>
              <div v-if="allowEdit" class="flex-grow min-w-48 max-w-64 p-1" >
                <input v-model="equipment.macAddress" type="text" class="w-full" placeholder="MAC address"
                @focus="prevValue = equipment.macAddress"
                @blur="prevValue !== equipment.macAddress ? updateBox('macAddress',equipment.macAddress,prevValue) : null"
                @keydown.enter="$event.target.blur()"
                >
              </div>
              <div v-else class="flex-grow min-w-fit p-1 text-left whitespace-normal truncate">{{ equipment.macAddress }}</div>
            </div>
            <!-- Primary MAC name — a label only; blank shows as Main -->
            <div v-if="allowEdit && equipment.hasMacAddress" class="w-full flex items-center flex-wrap">
              <div class="w-auto min-w-fit text-left whitespace-normal p-1 mr-1">MAC Name:</div>
              <div class="flex-grow min-w-48 max-w-64 p-1">
                <input v-model="equipment.mainMacName" type="text" class="w-full" placeholder="main" maxlength="25"
                @focus="prevValue = equipment.mainMacName"
                @blur="(prevValue || '') !== (equipment.mainMacName || '') ? updateBox('mainMacName',equipment.mainMacName,prevValue) : null"
                @keydown.enter="$event.target.blur()"
                >
              </div>
            </div>
        <!-- Other MACs: add by name, rename (Network data follows), or remove any (catalog ones too), on this box only -->
            <div v-if="equipment.hasMacAddress && ((Array.isArray(equipment.macAddresses) && equipment.macAddresses.length > 0) || allowEdit)" class="w-full flex flex-col flex-grow self-stretch min-h-0">
                <div class="w-full flex items-center flex-nowrap flex-shrink-0">
                    <div class="w-auto min-w-fit text-left whitespace-normal p-1 mr-1 shrink-0">Other MAC Addresses:</div>
                    <div v-if="canManageSlots" class="flex-grow min-w-0 p-1">
                        <input v-model="newMacName" type="search" class="w-full" placeholder="Add MAC (name)" maxlength="25" @blur="addMac" @keydown.enter="$event.target.blur()" />
                    </div>
                </div>
                <div class="w-full min-h-32 flex flex-grow self-stretch rounded-md border">
                    <div v-if="Array.isArray(equipment.macAddresses) && equipment.macAddresses.length > 0" class="w-full min-h-0 p-1 max-h-32 md:max-h-48 overflow-y-auto flex-grow self-stretch">
                        <div v-for="mac in (equipment.macAddresses || [])" :key="mac._id" class="w-full p-1">
                            <div class="w-full flex items-center flex-wrap">
                                <div v-if="allowEdit" class="flex-grow p-1 min-w-32 max-w-48">
                                    <input v-model="mac.name" type="text" class="w-full" placeholder="MAC name" maxlength="25"
                                        @focus="prevValue = mac.name"
                                        @blur="prevValue !== mac.name && updateArrayEntry('macAddresses', mac._id, { name: mac.name }, prevValue, 'name')"
                                        @keydown.enter="$event.target.blur()"
                                    />
                                </div>
                                <div v-else class="flex-grow text-left whitespace-normal truncate text-sky p-1">{{ camelCaseToLabel(mac.name) }}</div>
                                <div class="flex flex-nowrap items-center flex-grow">
                                    <div class="w-full p-1 min-w-36 max-w-64">
                                        <input v-if="allowEdit" v-model="mac.macAddress" type="text" class="w-full" placeholder="MAC address"
                                            @focus="prevValue = mac.macAddress"
                                            @blur="prevValue !== mac.macAddress && updateArrayEntry('macAddresses', mac._id, { macAddress: mac.macAddress }, prevValue, 'macAddress')"
                                            @keydown.enter="$event.target.blur()"
                                        />
                                        <div v-else class="w-full p-1 whitespace-normal truncate">{{ mac.macAddress }}</div>
                                    </div>
                                    <div v-if="canManageSlots" class="p-1 flex-shrink-0">
                                        <ButtonWithConfirm isDelete :popperContent="`Remove ${camelCaseToLabel(mac.name)} and its Network data`" @confirmed="deleteMacEntry(mac._id)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="w-full p-1 text-left whitespace-normal truncate">No other MACs</div>
                </div>
            </div>
          </div>
        <!-- Properties -->
          <div v-if="!equipment.multiBox" class="w-full flex-col md:w-1/3 md:min-w-44 p-1 flex md:justify-start items-start md:items-center self-stretch">
            <div class="w-full flex items-center flex-nowrap">
              <div class="w-auto min-w-fit text-left whitespace-normal text-sky p-1 mr-1 shrink-0">Properties:</div>
              <div v-if="allowEdit" class="flex-grow min-w-0 p-1">
                <input v-model="newPropertyName" type="search" class="w-full" placeholder="Add property (name)" maxlength="50" @blur="addProperty" @keydown.enter="$event.target.blur()" />
              </div>
            </div>
            <div class="w-full min-h-32 flex-grow self-stretch rounded-md border">
                <div v-if="Array.isArray(equipment.properties) && equipment.properties.length > 0" class="w-full min-h-0 p-1 rounded-md max-h-32 md:max-h-48 overflow-y-auto flex-grow self-stretch">
                    <div v-for="property in (equipment.properties || [])" :key="property._id" class="w-full p-1">
                        <div class="w-full flex items-center flex-wrap">
                            <div class="flex-grow text-left whitespace-normal truncate text-sky p-1">{{ camelCaseToLabel(property.name) }}</div>
                            <div class="flex flex-nowrap items-center flex-grow">
                              <div class="w-full p-1 min-w-36 max-w-64">
                                <input v-if="allowEdit" v-model="property.value" type="text" class="w-full" placeholder="property value"
                                    @focus="prevValue = property.value"
                                    @blur="prevValue !== property.value && updateArrayEntry('properties', property._id, { value: property.value }, prevValue, 'value')"
                                    @keydown.enter="$event.target.blur()"
                                />
                                <div v-else class="w-full p-1 whitespace-normal truncate">{{ property.value }}</div>
                              </div>
                              <div v-if="allowEdit && property.local" class="p-1 flex-shrink-0">
                                <ButtonWithConfirm isDelete :popperContent="`Remove ${camelCaseToLabel(property.name)}`" @confirmed="deletePropertyEntry(property._id)" />
                              </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="w-full p-1 text-left whitespace-normal truncate">No properties found</div>
            </div>
          </div>
        </div>
<!-- Network (read-only — edited on the Network tab). One block per MAC that holds data; network[]
     is sparse, so a box nobody has filled in yet renders nothing at all here. -->
        <div v-if="networkMacs.length" class="w-full flex flex-col px-1">
          <div class="w-full text-left whitespace-normal text-sky font-semibold border-b p-1">Network:</div>
          <div v-for="mac in networkMacs" :key="mac.macName" class="w-full border-b p-1">
        <!-- Labels are min-w-fit, which resolves to the space available when the text is longer than
             that — so a label can claim the whole pair and leave the value 0px wide. Every value
             carries a real floor and every pair wraps, so a long label (a MAC slot name is up to 25
             chars, a custom field name up to 50) drops its value onto the next line instead. -->
            <div class="w-full flex items-center flex-wrap">
              <div class="w-auto min-w-fit text-left whitespace-normal break-words text-indigo p-1 mr-1">{{ mac.label }}:</div>
              <div class="flex-grow min-w-32 text-left whitespace-normal break-words p-1">{{ mac.macAddress }}</div>
            </div>
            <div class="w-full flex items-start flex-wrap">
              <div v-for="item in mac.values" :key="item.key" class="w-auto min-w-44 flex-grow flex flex-wrap items-start p-1">
                <div class="w-auto min-w-fit text-left whitespace-normal break-words text-sky mr-1">{{ item.label }}:</div>
                <div class="flex-grow min-w-24 text-left whitespace-normal break-words">{{ item.value }}</div>
              </div>
            </div>
          </div>
        </div>
        </div>
      <div v-if="!dontShowMultiBoxDetails && showWarehouseActions && isWarehouse && equipment && typeof equipment === 'object'" class="w-full flex flex-wrap items-center justify-center panel">
        <div class="p-2">
          <ButtonWithConfirm
            :popperContent="'Return to Stock'"
            :buttonText="'Restock'"
            :iconComponent="ReplyIcon"
            :buttonClass="'bn-solid-sky mx-1 px-1'"
            :disabled="isWorking || !isShipped || outofCommission"
            @confirmed="returnToStock"
          />
        </div>
        <div class="p-2">
          <ButtonWithConfirm
            :popperContent="'Mark as Damaged'"
            :buttonText="'Damaged'"
            :iconComponent="BanIcon"
            :buttonClass="`${equipment?.returnDisposition === 'damaged' ? 'bn-solid-red' : 'bn-red'} mx-1 px-1`"
            :disabled="isWorking || outofCommission"
            @confirmed="markAsDamaged"
          />
        </div>
      </div>
    </div>
    <Modal v-if="showHistory" title="History" @closeModal="showHistory = false">
      <template v-slot:content>
        <History  :logs="equipment?.logs" />
      </template>
    </Modal>
<!-- Kit Damage Selection -->
    <Modal v-if="kitDamageSelection" title="Select Damaged Items" @closeModal="kitDamageSelection = null">
      <template v-slot:content>
        <div class="w-full flex flex-col items-center p-4 gap-3">
          <div class="text-sm text-center">Select which items in this kit are damaged:</div>
          <div class="w-full max-w-lg flex flex-col gap-1">
            <div v-for="item in kitDamageSelection.items" :key="item._id"
              class="w-full flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-sky-500/10"
              :class="kitDamageSelection.selectedIds.includes(item._id) ? 'text-green' : ''"
              @click="kitDamageSelection.selectedIds.includes(item._id) ? kitDamageSelection.selectedIds = kitDamageSelection.selectedIds.filter(id => id !== item._id) : kitDamageSelection.selectedIds.push(item._id)"
            >
              <CheckIcon v-if="kitDamageSelection.selectedIds.includes(item._id)" class="w-5 h-5 flex-shrink-0 text-green" />
              <div v-else class="w-5 h-5 flex-shrink-0" />
              <div class="flex-grow text-left whitespace-normal">
                <span class="font-semibold">Box {{ formatBoxNumber(item.boxNumber, item.boxYear) }}</span>
                <span v-if="item.manufacturer" class="ml-1">— {{ String(item.manufacturer).toUpperCase() }}</span>
                <span v-if="item.partNumber" class="ml-1 opacity-70">{{ String(item.partNumber).toUpperCase() }}</span>
              </div>
            </div>
          </div>
          <button class="bn-solid-sky" :disabled="isWorking || !kitDamageSelection.selectedIds.length" @click="markKitItemsDamaged">
            Mark {{ kitDamageSelection.selectedIds.length }} Item{{ kitDamageSelection.selectedIds.length !== 1 ? 's' : '' }} as Damaged
          </button>
        </div>
      </template>
    </Modal>
<!-- Damage Report -->
    <Modal v-if="damageReportEquipmentId" title="Damage Report" @closeModal="damageReportEquipmentId = null">
      <template v-slot:content>
        <DamageReport :equipmentId="damageReportEquipmentId" :showHeading="false" :broadcastSource="EDIT_BOX_SOURCE" @updated="getEquipment('silent')" @decisionMade="damageReportEquipmentId = null; getEquipment('silent')" />
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, inject, onUnmounted, computed } from 'vue'
import api from '@/api'
import { parseISO, format, isValid, isPast} from 'date-fns'
import { camelCaseToLabel, capitalizeString, formatBoxNumber, broadcastChange, isOwnBroadcast, roomLabel } from '@/shared'
import {ReplyIcon, BanIcon, DocumentIcon, CheckIcon, XIcon} from '@heroicons/vue/solid'
import MdManageHistory from '@/components/customIcons/MdManageHistory.vue'
import History from '@/components/ui/History.vue'
import ButtonWithConfirm from '@/components/ui/ButtonWithConfirm.vue'
import DamageReport from '@/components/logistics/DamageReport.vue'
// Name lent to this box's Damage Report, so its decisions reach this tab (the tracker, tiles and Search behind
// it) while this box skips them: it already reloads from the dialog's emits. See broadcastChange in shared.js.
const EDIT_BOX_SOURCE = 'EditBox'

export default {
  components: { MdManageHistory, History, ButtonWithConfirm, DocumentIcon, CheckIcon, XIcon, DamageReport },
  props: {
    equipmentId: {
      type: String,
      default: null
    },
    allowEdit: {
      type: Boolean,
      default: false
    },
    showWarehouseActions: {
      type: Boolean,
      default: false
    },
    labelView: {
      type: Boolean,
      default: false
    },
    external: {
      type: Boolean,
      default: false
    },
    project: {
      type: Object,
      default: null
    }
  },
  emits:['updateEquipment'],
  setup(props,{emit}) {
    const global = inject('global')
    const {setModalBlocked,authenticated} = global
    const isWorking = ref(false)
    const responseError = ref(null)
    const equipment = ref(null)
    const showHistory = ref(false)
    const kitDamageSelection = ref(null)
    const damageReportEquipmentId = ref(null)
    const newPropertyName = ref('')
    const newMacName = ref('')
    const newSerialName = ref('')
    let prevValue = null

    const isPrimary = computed(() => authenticated.value.organization?.orgType?.includes('primary'))
    const isWarehouse = computed(() => isPrimary.value && (
      authenticated.value.role?.accessModel?.warehouseQueue > 0 ||
      authenticated.value.role?.accessModel?.rackQueue > 0 ||
      authenticated.value.role?.accessModel?.receiving > 0
    ))
    // Adding or removing a named serial or MAC — the server's tier (projects 2 or warehouseQueue 2); rename and values stay with allowEdit
    const canManageSlots = computed(() => props.allowEdit && (
      authenticated.value.role?.accessModel?.projects >= 2 ||
      authenticated.value.role?.accessModel?.warehouseQueue >= 2
    ))

    const isShipped = computed(() => equipment.value?.shipmentId?.shipmentState > 1)
    const outofCommission = computed(() => !!equipment.value?.returnDisposition)

    const dontShowMultiBoxDetails = computed(() => props.labelView && equipment.value?.multiBox)

    // One block per MAC that actually holds network data, for READ-ONLY display — these fields are
    // edited on the Network tab, never here. Values come straight off the equipment doc: a custom
    // field carries its own name in network[].custom[], so nothing needs the project's column list.
    const networkMacs = computed(() => {
      const eq = equipment.value
      const project = eq?.projectId
      // Install project = none of the five warehouse-type flags, the same rule as EquipmentView's
      // isNetworkEligible. Requires a real _id first: without one every flag reads false, which
      // would pass a project we know nothing about. Externally the payload carries no network[]
      // at all (EXTERNAL_HIDDEN_EQUIPMENT_FIELDS), so there is nothing to hide separately.
      const isInstall = !!project?._id && !(
        project.isCustomerHold || project.isCustomerReceivingHold || project.isReceivingHold ||
        project.isInternalInventory || project.isFulfillment || project.isFulfillmentOrder
      )
      // hasMacAddress is the boolean that says whether this device carries a MAC at all — the same
      // row source the Network tab uses. The address STRING being blank means nobody has typed it
      // yet, not that the device has no MAC, so it is never the test.
      const network = Array.isArray(eq?.network) ? eq.network : []
      if (!isInstall || !eq?.hasMacAddress || !network.length) return []

      // Stored lowercase; the Network tab displays every value but the category uppercase.
      const upper = (v) => (v || '').toUpperCase()

      const build = (macName, label, macAddress) => {
        const entry = network.find((n) => n.macName === macName)
        if (!entry) return null
        // Keyed separately from the label: a custom column named 'category' would render the label
        // 'Category' twice, and updateEquipmentNetworkEntry deliberately does not validate custom
        // names against the reserved list. The label filter drops a custom stored with a blank name,
        // which would otherwise render a bare ':'.
        const values = [
          { key: 'category', label: 'Category', value: camelCaseToLabel(entry.category) },
          { key: 'deviceId', label: 'Device ID', value: upper(entry.deviceId) },
          { key: 'ipid', label: 'IPID', value: upper(entry.ipid) },
          { key: 'hostName', label: 'Host Name', value: upper(entry.hostName) },
          { key: 'ipAddress', label: 'IP Address', value: upper(entry.ipAddress) },
          ...(entry.custom || []).map((c) => ({ key: `custom:${c.name}`, label: camelCaseToLabel(c.name), value: upper(c.value) }))
        ].filter((v) => !!v.value && !!v.label)
        // An entry created for a field that was later cleared has nothing left to show.
        return values.length ? { macName, label, macAddress: upper(macAddress), values } : null
      }

      // 'main' is the sentinel for the macAddress SCALAR; every other entry keys off a slot name.
      return [
        build('main', eq.mainMacName ? camelCaseToLabel(eq.mainMacName) : 'Main', eq.macAddress),
        ...(eq.macAddresses || []).map((m) => build(m.name, camelCaseToLabel(m.name), m.macAddress))
      ].filter(Boolean)
    })

    onMounted(() => {
      window.addEventListener("data_change_from_socket",handleSocketChange)
      getEquipment()
    })

    onUnmounted(() => {
      window.removeEventListener("data_change_from_socket",handleSocketChange)
    })

    const getEquipment = async (type) => {
      if (!props.equipmentId) {
        responseError.value = 'No equipment id provided'
        return
      }
      if(type !== 'silent') {
        isWorking.value = true
        setModalBlocked(true)
      }
      responseError.value = null
      const useExternal = props.external && props.project?._id && props.project?.secureCode
      const getUrl = useExternal
        ? `external/logistics/equipment/${props.project._id}/${props.project.secureCode}/${props.equipmentId}`
        : `logistics/equipment/${props.equipmentId}`
      await api
        .get(getUrl)
        .then((res) => {
          equipment.value = res.data?.data ?? null
        })
        .catch((err) => {
            let error = err.response?.data?.error || err.message
            type !== 'silent' ? responseError.value = error : console.error(error)
        })
        .finally(() => {
          isWorking.value = false
          setModalBlocked(false)
        })
    }

    const updateBox = async (key, value, prevValue) => {
      setModalBlocked(true)
      isWorking.value = true
      responseError.value = null
      const body = { [key]: value }
      await api
        .put(`/logistics/equipment/${props.equipmentId}`, body)
        .then((res) => {
          if (res.data?.data && typeof res.data.data === 'object' && !Array.isArray(res.data.data)) {
            equipment.value = res.data.data
            broadcastChange('equipment', { equipmentId: props.equipmentId, projectId: res.data?.data?.projectId?._id, metaDataChanged: true }, EDIT_BOX_SOURCE)
            emit('updateEquipment', res.data.data)
          }
        })
        .catch((err) => {
          responseError.value = err.response?.data?.error || err.message
          if (equipment.value && prevValue !== undefined) {
            equipment.value[key] = Array.isArray(prevValue) ? JSON.parse(JSON.stringify(prevValue)) : prevValue
          }
        })
        .finally(() => {
          isWorking.value = false
          setModalBlocked(false)
        })
    }

    const updateArrayEntry = async (path, entryId, body, prevValue, revertKey) => {
      setModalBlocked(true)
      isWorking.value = true
      responseError.value = null
      await api
        .put(`/logistics/equipment/${props.equipmentId}/${path}/${entryId}`, body)
        .then((res) => {
          if (res.data?.data && typeof res.data.data === 'object' && !Array.isArray(res.data.data)) {
            equipment.value = res.data.data
            broadcastChange('equipment', { equipmentId: props.equipmentId, projectId: res.data?.data?.projectId?._id, metaDataChanged: true }, EDIT_BOX_SOURCE)
            emit('updateEquipment', res.data.data)
          }
        })
        .catch((err) => {
          responseError.value = err.response?.data?.error || err.message
          const arr = equipment.value?.[path]
          const entry = Array.isArray(arr) ? arr.find((e) => e._id === entryId) : null
          if (entry && prevValue !== undefined) entry[revertKey] = prevValue
        })
        .finally(() => {
          isWorking.value = false
          setModalBlocked(false)
        })
    }

    const returnToStock = async () => {
      isWorking.value = true
      responseError.value = null
      await api
      .put(`logistics/equipment/returnToInventory/${equipment.value?._id}`)
      .then((res) => {
        if(res.data?.data && typeof res.data.data === 'object') {
         equipment.value = res.data.data
          broadcastChange('equipment', {equipmentId:res.data?.data?._id,projectId:res.data?.data?.projectId?._id}, EDIT_BOX_SOURCE)
        }
      })
      .catch((err) => {
        responseError.value = err.response?.data?.error || err.message
      })
      .finally(() => {
        isWorking.value = false
      })
    }

    const markSingleDamaged = async (id, openReport = true) => {
      // Damaging a unit of a sealed carton unseals the whole carton server-side. Flag it so the tracker
      // refetches the carton instead of patching this one unit and leaving its carton-mates Sealed.
      // Read before the call — the returned record is already unsealed.
      const wasSealed = String(id) === String(equipment.value?._id) && !!equipment.value?.boxSealed
      isWorking.value = true
      responseError.value = null
      let resultId = null
      await api
      .put(`logistics/equipment/markAsDamaged/${id}`)
      .then((res) => {
        if(res.data?.data && typeof res.data.data === 'object') {
          if (String(id) === String(equipment.value?._id)) {
            equipment.value = res.data.data
          }
          resultId = res.data?.data?._id
          broadcastChange('equipment', {equipmentId:res.data?.data?._id,projectId:res.data?.data?.projectId?._id,isDamageReport:true, ...(wasSealed ? { boxUnsealed: true } : {})}, EDIT_BOX_SOURCE)
        }
      })
      .catch((err) => {
        responseError.value = err.response?.data?.error || err.message
      })
      .finally(() => {
        isWorking.value = false
      })
      if (openReport && resultId) damageReportEquipmentId.value = resultId
    }

    const markAsDamaged = async () => {
      const eq = equipment.value
      if (!eq) return
      if (eq.isKit && Array.isArray(eq.kitItems) && eq.kitItems.length) {
        kitDamageSelection.value = {
          items: eq.kitItems,
          selectedIds: [],
          kitContainerId: eq._id
        }
        return
      }
      await markSingleDamaged(eq._id, true)
    }

    const markKitItemsDamaged = async () => {
      if (!kitDamageSelection.value) return
      const { selectedIds, kitContainerId } = kitDamageSelection.value
      kitDamageSelection.value = null
      for (const eqId of selectedIds) {
        await markSingleDamaged(eqId, false)
      }
      damageReportEquipmentId.value = kitContainerId
    }

    const openDamageReport = () => {
      if (!equipment.value?._id) return
      damageReportEquipmentId.value = equipment.value._id
    }

    const handleSocketChange = (e) => {
      // Its own saves and Damage Report announce under this name; the box already shows (or reloads) the result.
      if (isOwnBroadcast(e, EDIT_BOX_SOURCE)) return
      if(e.detail?.type === 'equipment' && e.detail?.data?.equipmentId === props.equipmentId) {
        getEquipment('silent')
        return
      }
      // Column-wide network writes (clear / replace / rename / remove a field) announce per PROJECT,
      // not per box -- they can touch any number of boxes, so there is no id list to filter on. The
      // network block above is read-only display, so a stale one is silently wrong. One extra fetch
      // of one box, only while a box is open and only when someone runs a column operation.
      if(e.detail?.type === 'network' && e.detail?.data?.projectId
         && e.detail.data.projectId === (equipment.value?.projectId?._id ?? equipment.value?.projectId)) {
        getEquipment('silent')
      }
    }

    // One add or remove of a property, serial or MAC: the returned box replaces the shown one and other screens hear it.
    // `onSaved` clears the input used.
    const saveEntry = async (request, onSaved) => {
      setModalBlocked(true)
      isWorking.value = true
      responseError.value = null
      await request()
        .then((res) => {
          if (res.data?.data && typeof res.data.data === 'object' && !Array.isArray(res.data.data)) {
            equipment.value = res.data.data
            onSaved?.()
            broadcastChange('equipment', { equipmentId: props.equipmentId, projectId: res.data?.data?.projectId?._id, metaDataChanged: true }, EDIT_BOX_SOURCE)
            emit('updateEquipment', res.data.data)
          }
        })
        .catch((err) => {
          responseError.value = err.response?.data?.error || err.message
        })
        .finally(() => {
          isWorking.value = false
          setModalBlocked(false)
        })
    }

    const addProperty = async () => {
      const name = newPropertyName.value.trim()
      if (!name) return
      if ((equipment.value?.properties || []).some((p) => p.name && String(p.name).trim().toLowerCase() === name.toLowerCase())) {
        responseError.value = `A property named '${name}' already exists`
        return
      }
      await saveEntry(() => api.post(`/logistics/equipment/${props.equipmentId}/properties`, { name, value: '' }), () => { newPropertyName.value = '' })
    }

    // A named MAC on this box alone, empty until its address is typed. Names are stored lowercase, as on the server.
    const addMac = async () => {
      const name = newMacName.value.trim().toLowerCase()
      if (!name) return
      if (name === 'main' || name === (equipment.value?.mainMacName || '') || (equipment.value?.macAddresses || []).some((m) => m.name === name)) {
        responseError.value = `A MAC named '${name}' already exists`
        return
      }
      await saveEntry(() => api.post(`/logistics/equipment/${props.equipmentId}/macAddresses`, { name }), () => { newMacName.value = '' })
    }

    // A named serial on this box alone, as addMac.
    const addSerial = async () => {
      const name = newSerialName.value.trim().toLowerCase()
      if (!name) return
      if ((equipment.value?.serialNumbers || []).some((s) => s.name === name)) {
        responseError.value = `A serial named '${name}' already exists`
        return
      }
      await saveEntry(() => api.post(`/logistics/equipment/${props.equipmentId}/serialNumbers`, { name }), () => { newSerialName.value = '' })
    }

    // Removes a serial, a MAC (with its Network data) or a property added here. ButtonWithConfirm asks first.
    const deleteSerialEntry = async (entryId) => {
      if (entryId) await saveEntry(() => api.delete(`/logistics/equipment/${props.equipmentId}/serialNumbers/${entryId}`))
    }
    const deleteMacEntry = async (entryId) => {
      if (entryId) await saveEntry(() => api.delete(`/logistics/equipment/${props.equipmentId}/macAddresses/${entryId}`))
    }
    const deletePropertyEntry = async (entryId) => {
      if (entryId) await saveEntry(() => api.delete(`/logistics/equipment/${props.equipmentId}/properties/${entryId}`))
    }

    return {
      EDIT_BOX_SOURCE,
      formatBoxNumber,
      roomLabel,
      isWorking,
      responseError,
      equipment,
      parseISO,
      format,
      isValid,
      camelCaseToLabel,
      capitalizeString,
      showHistory,
      updateBox,
      prevValue,
      updateArrayEntry,
      newPropertyName,
      addProperty,
      deletePropertyEntry,
      canManageSlots,
      newMacName,
      addMac,
      deleteMacEntry,
      newSerialName,
      addSerial,
      deleteSerialEntry,
      isPast,
      isWarehouse,
      ReplyIcon,
      BanIcon,
      isShipped,
      outofCommission,
      returnToStock,
      markAsDamaged,
      markKitItemsDamaged,
      kitDamageSelection,
      damageReportEquipmentId,
      getEquipment,
      dontShowMultiBoxDetails,
      networkMacs,
      openDamageReport,
    }
  }
}
</script>

<style scoped>
</style>
